import React, { FC, useEffect, useMemo, useState } from 'react'
import { useAutoPronounce } from '../Utilities/useAutoPronounce'
import { useTranslation } from 'react-i18next'
import './CardsPlay.less'
import { useAppDispatch, useAppSelector } from '../hooks'
import { Direction, GameType, getNextCard, setCurrentCard, startGame } from '../Store/GameSlice'
import { toast } from 'react-toastify'
import { DisplayTool } from './VocabularyTools'
import { updateRow } from '../Store/VocabulariesSlice'
import { pronounce } from '../Store/PronounceSlice'

import CardsPlayTools from './CardsPlayTools'
import CardsPlayMessageDisplay from './CardsPlayMessageDisplay'
import CardsPlayControls from './CardsPlayControls'
import CardsPlayCardDisplay from './CardsPlayCardDisplay'
import CardsPlayProgress from './CardsPlayProgress'

export interface PlayPreferences {
  direction: Direction
  showPinyin?: boolean
  forceNewGame: boolean
  gameType: GameType
  infoLanguage?: string
  secretLanguage?: string
}

interface CardsPlayProps {
  preferences: PlayPreferences
  onDemo: () => void
  onSetup: () => void
  onFinished: () => void
}

type InteractionState = 'showQuestion' | 'evaluate.normal' | 'evaluate.wrong' | 'disabled' | 'edit'

const CardsPlay: FC<CardsPlayProps> = ({ preferences, onDemo, onSetup, onFinished }) => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const [loaded, setLoaded] = useState(true)
  const [interactionState, setInteractionState] = useState<InteractionState>('disabled')

  const currentCardSelector = useAppSelector(state => state.game.currentGame?.state.current_card)
  const currentCard = useMemo(() => currentCardSelector || {
    info: '',
    secret: '',
    row_id: 0,
  }, [currentCardSelector])
  const coverPosition = useMemo(() => (interactionState === 'showQuestion') ? 'up' : 'down', [interactionState])

  const currentVocabularyId = useAppSelector(state => state.vocabularies.currentVocabulary?.id)

  // Start game
  const doStartGame = async () => {
    const v = Number(new URLSearchParams(window.location.search).get('v'))
    if (preferences.gameType.endsWith('BlockCardsGame')) {
      const vocabularyId = v || currentVocabularyId
      if (!vocabularyId) {
        toast.error(t('cardShow.errorGettingVocabularyId'))
        setInteractionState('disabled')
        return
      }
      return dispatch(startGame({
        vocabularyId,
        ...preferences,
      }))
    }
    return dispatch(startGame({
      ...preferences,
    }))
  }
  useEffect(() => {
    (async () => {
      console.log('start play', preferences)
      const response = await doStartGame()
      if (!startGame.fulfilled.match(response)) {
        toast.error(t('cardShow.errorStartingGame'))
        setInteractionState('disabled')
        return
      }
      setInteractionState('showQuestion')
      autoPronounceInfo()
    })()
  }, [])

  // Finish game
  useEffect(() => {
    if (interactionState === 'showQuestion' && !currentCard.info) {
      onFinished()
    }
  }, [currentCard, interactionState])

  // Game flow
  const showSecret = () => {
    setInteractionState('evaluate.normal')
    autoPronounceSecret()
  }
  const onAnswer = async (answer: boolean) => {
    const previousState = interactionState
    setInteractionState('showQuestion')
    setLoaded(false)
    const result = await dispatch(getNextCard({ answer }))
    setLoaded(true)
    if (!getNextCard.fulfilled.match(result)) {
      toast.error(t('cardShow.errorGettingNextCard'))
      setInteractionState(previousState)
      return
    }
    autoPronounceInfo()
  }
  const onCorrect = async () => {
    await onAnswer(true)
  }
  const onWrong = async () => {
    await onAnswer(false)
  }
  const onQuestionMark = () => {
    setInteractionState('evaluate.wrong')
    autoPronounceSecret()
  }
  const onOk = async () => {
    await onWrong()
  }

  /* Pronunciations */
  const [displayTools, setDisplayTools] = useState<DisplayTool>('')
  const infoLanguage = useMemo(() => preferences.infoLanguage || '', [preferences.infoLanguage])
  const secretLanguage = useMemo(() => preferences.secretLanguage || '', [preferences.secretLanguage])
  const pronounceInfo = () => {
    if (!infoLanguage) return Promise.resolve()
    return dispatch(pronounce({ text: currentCard.info, language: infoLanguage }))
  }
  const pronounceSecret = () => {
    if (!secretLanguage) return Promise.resolve()
    if (preferences.gameType === 'NumbersGame' && secretLanguage === 'ar') {
      return dispatch(pronounce({ text: currentCard.info, language: 'ar' }))
    }
    return dispatch(pronounce({ text: currentCard.secret, language: secretLanguage }))
  }
  const infoPronounced = useAppSelector(state => {
    const pronunciations = state.preferences.preferences?.cardPronunciations || {}
    if (!infoLanguage) return false
    if (preferences.gameType === 'NumbersGame') return false
    if (!(infoLanguage in pronunciations)) return true
    return pronunciations[infoLanguage]
  })
  const secretPronounced = useAppSelector(state => {
    const pronunciations = state.preferences.preferences?.cardPronunciations || {}
    if (!secretLanguage) return false
    if (!(secretLanguage in pronunciations)) return true
    return pronunciations[secretLanguage]
  })
  const autoPronounceInfo = useAutoPronounce(infoPronounced, pronounceInfo)
  const autoPronounceSecret = useAutoPronounce(secretPronounced, pronounceSecret)

  /* Edit card */
  const onEdit = () => {
    if (interactionState === 'edit') {
      setInteractionState('evaluate.normal')
      return
    }
    setDisplayTools('')
    setInteractionState('edit')
  }
  const [editInfo, setEditInfo] = useState('')
  const [editSecret, setEditSecret] = useState('')

  useEffect(() => {
    if (interactionState !== 'edit') return
    setEditInfo(currentCard.info)
    setEditSecret(currentCard.secret)
  }, [interactionState])

  const onSaveEdit = async () => {
    let left, right
    if (preferences.direction === 'ltr') {
      left = editInfo
      right = editSecret
    } else {
      left = editSecret
      right = editInfo
    }
    dispatch(setCurrentCard({ info: editInfo, secret: editSecret }))
    const results = await dispatch(updateRow({ id: currentCard.row_id, left, right }))
    if (!updateRow.fulfilled.match(results)) {
      toast.error(t('cardShow.errorSavingRow'))
      return
    }
    setInteractionState('evaluate.normal')
  }
  const onCancelEdit = () => {
    setInteractionState('evaluate.normal')
  }

  return (
    <div className={`play ${preferences.gameType}`}>
      <div></div>
      {/* This is a hack to fix the layout */}
      <div className="cardshow">
        <CardsPlayCardDisplay
          currentCard={currentCard}
          coverPosition={coverPosition}
          loaded={loaded}
          interactionState={interactionState}
          showSecret={showSecret}
          setEditInfo={setEditInfo}
          setEditSecret={setEditSecret}
          displayTools={displayTools}
          infoLanguage={infoLanguage}
          secretLanguage={secretLanguage}
        />
        <div className="navigate">
          <CardsPlayControls
            interactionState={interactionState}
            gameType={preferences.gameType}
            onCorrect={onCorrect}
            onWrong={onWrong}
            onQuestionMark={onQuestionMark}
            onOk={onOk}
            onCancelEdit={onCancelEdit}
            onSaveEdit={onSaveEdit}
          />
          <CardsPlayProgress />
        </div>

        <CardsPlayMessageDisplay />
      </div>

      <CardsPlayTools
        displayTools={displayTools}
        setDisplayTools={setDisplayTools}
        onEdit={onEdit}
        onDemo={onDemo}
        onSetup={onSetup}
      />
    </div>
  )
}

export default CardsPlay
